import dayjs from 'dayjs';

import { ReportsStatus } from '@hrms/reports/pages/reports-view/types';
import { OptionsInterface } from '../component/form-component';
import { preferenceStore } from '../component/user-preference/store';
import {
  formatCompleteDate,
  formatCompleteDateandTime,
} from './dateConversion';
import { ToastSeverity } from '../contexts/toast/constant';
import { ToastContextProps } from '../contexts/toast/types';

export const toTitleCase = (text: string): string => {
  if (typeof text !== 'string' || text.length === 0) {
    return '';
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const pickIdsFromOptions = (options: OptionsInterface[]): string[] => {
  if (options && Array.isArray(options) && options.length > 0)
    return options.map((option) => option?.id)?.filter((id) => id);
  return [];
};

export const getDefaultOptions = (options: string[]): OptionsInterface[] => {
  if (options && Array.isArray(options) && options.length > 0)
    return options.map((option) => {
      return { id: option, label: option };
    });
  return [];
};

export function formatName(name: string) {
  // Replace underscores with spaces and convert to lowercase
  let formatted = name.replace(/_/g, ' ').toLowerCase();
  // Capitalize the first letter of each word
  formatted = formatted.replace(/\b\w/g, (char) => char.toUpperCase());
  return formatted;
}

export function dateFormat(utcDateString: string) {
  const utcDate = new Date(utcDateString); // Convert to Date object
  const year = utcDate.getFullYear();
  const month = String(utcDate.getMonth() + 1).padStart(2, '0'); // Month is 0-based
  const day = String(utcDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`; // Format as YYYY-MM-DD
}

export const removeLeadingZeros = (numStr: string) => {
  return parseFloat(numStr);
};

export const COMMON_ERROR_MESSAGE = "Couldn't fetch data";

export const getReportFileName = (
  usecase: string,
  date: string,
  status?: string,
) =>
  `${usecase}_${dayjs(date).format('DD-MMM-YYYY_h:mmA')}${
    status === ReportsStatus.FAILED ? '_error' : ''
  }`;

// Validation patterns
export const PHONE_NUMBER_PATTERN = /^[0-9]{10}$/;
export const UAN_PATTERN = /^[0-9]{12}$/;
export const IFSC_PATTERN = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const ACCOUNT_NUMBER_PATTERN = /^[0-9]{9,18}$/;

export const getByPath = (obj: any, path: string) => {
  if (!path) return undefined;
  return path.split('.').reduce((acc: any, key: string) => acc?.[key], obj);
};

export const resolveDataFromSlip = (
  data: any,
  slip: Record<string, any>,
): any => {
  if (Array.isArray(data)) return data.map((v) => resolveDataFromSlip(v, slip));
  if (data && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, resolveDataFromSlip(v, slip)]),
    );
  }
  if (typeof data === 'string') {
    const picked = getByPath(slip, data);
    return picked === undefined ? data : picked;
  }
  return data;
};

export const calculateTotalRows = (
  pageNo: number,
  hasNext: boolean | undefined,
  rowsPerPage: number,
): number => {
  const nextExists = hasNext ?? false;
  return (pageNo + (nextExists ? 2 : 1)) * rowsPerPage;
};

export const normalizeNullableString = (value?: string | null) => {
  return value == null || value === '' ? null : value;
};

export const isSelectedTenantMH = (): boolean => {
  const { preference } = preferenceStore.getState();
  return preference?.selectedTenant?.label === 'MH';
};

export const renderText = (value: string) => value || '-';

export const renderDate = (value: string) =>
  value ? formatCompleteDate(value) : '-';

export const renderDateTime = (value: string) =>
  value ? formatCompleteDateandTime(value) : '-';

const DOCUMENT_TYPE_ICON_MAP: Record<string, string> = {
  selfie: 'camera-02',
  aadhaar: 'AADHAAR',
  pan: 'PAN',
  offerletter: 'file-05',
};

const DOCUMENT_TYPE_ICON_KEYS = Object.keys(DOCUMENT_TYPE_ICON_MAP);

export const getDocumentIcon = (docType?: string): string => {
  if (!docType) return 'file-05';

  const normalizedDocType = docType.toLowerCase();
  const icon = DOCUMENT_TYPE_ICON_MAP[normalizedDocType];
  if (icon) return icon;

  // e.g. "Aadhaar - Front/Back" → match "aadhaar"
  const matchedKey = DOCUMENT_TYPE_ICON_KEYS.find((key) =>
    normalizedDocType.includes(key),
  );
  return matchedKey ? DOCUMENT_TYPE_ICON_MAP[matchedKey] : 'file-05';
};

export const getCommaSeparatedIdsFromArray = (
  arr: any[] | undefined,
): string => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  return arr
    .map((item) => item?.id || item)
    .filter(Boolean)
    .join(',');
};

export const mapListToOptions = (
  data: Array<{ [key: string]: any }>,
  key: string,
): OptionsInterface[] => {
  return Array.isArray(data) && data.length > 0
    ? data.map((item: Record<string, string>) => ({
        id: item[key],
        label: item.name || item.label,
      }))
    : [];
};

export const showApiErrorToast = (
  openToast: ToastContextProps['openToast'],
  error: any,
  defaultMessage: string = 'Something went wrong',
) => {
  console.error(defaultMessage, error);
  const errData = error?.response?.data;
  openToast({
    severity: ToastSeverity.error,
    message:
      typeof errData === 'string'
        ? errData
        : errData?.message ?? defaultMessage,
  });
};

/** Returns a shallow copy of `obj` without the given keys (does not mutate). */
export const omitObjectKeys = <T extends Record<string, unknown>>(
  obj: T,
  keys: readonly string[],
): T => {
  const result = { ...obj };
  keys.forEach((key) => {
    delete result[key];
  });
  return result;
};
